<template>
  <a
    :href="listingUrl"
    class="relative block peer group"
    :title="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
  >
    <div class="sold-ribbon-container uppercase text-white" v-if="soldRibbon && listing.sold">
      <div class="sold-ribbon">Sold</div>
    </div>

    <div
      class="bg-gray-100 overflow-hidden"
      :style="`aspect-ratio: auto ${listing.images.primary_attachment_aspect_ratio};`"
    >
      <div
        v-if="listing.reprint && !listing.sold"
        class="absolute z-[2] top-5 left-5 py-2 px-4 flex items-center space-x-2 text-bt-blue-500 bg-white rounded-full shadow"
      >
        <FaIcon :icon="['far', 'image']" />
        <span class="font-semibold">Print</span>
      </div>
      <img
        v-intersect.once="loadFullImage"
        class="mx-auto w-full h-full object-cover transition-transform ease-in-out duration-700 group-hover:scale-110"
        :id="'ListingImg__' + listing.id"
        :src="imageSrc"
        :srcset="fullSrcset"
        sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
        :alt="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
        @load="fullSrcset ? handleFullLoad($event) : null"
        @error="fullSrcset ? handleFullError($event) : handleTinyError()"
      />
    </div>
    <div class="px-4 py-6">
      <div class="flex items-center">
        <strong class="font-normal text-sm uppercase mr-3 text-zinc-900 capitalize line-clamp-1">
          {{ listing.title | truncate(29, "...") }}
        </strong>

        <div class="ml-auto flex items-center">
          <Favourite :listing="listing" :guestFavourites="guestFavourites" />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-zinc-400 text-sm capitalize line-clamp-1">
          {{ listing.artist.full_name }}
        </span>
      </div>

      <div class="flex flex-row flex-wrap items-start text-zinc-400 text-xs mt-4">
        <template v-if="listing.reprint">
          <p>
            Print on Canvas,
            <template v-if="listing.reprint"> Multiple Sizes </template>
            <template v-else>
              {{ listing.dimensions_short }}
            </template>
          </p>
        </template>
        <template v-else>
          <p>
            <Medium :listing="listing" class="inline" />
            <span v-if="listing.frame">, Framed by Artist</span>
          </p>
          <p>, {{ listing.dimensions_short }}</p>
        </template>
      </div>

      <div class="justify-between flex items-center mt-5">
        <span class="text-black text-sm font-medium">
          <span>{{ priceLabel }}</span>
          <span class="text-xs mt-2 ml-2" v-if="listing.price_with_discount">
            <span class="font-normal line-through text-zinc-400 mr-4">{{ listing.price.format }}</span>
            <span class="bg-red-50 text-red-900 p-2 rounded-md">{{ listing.discount_percentage | discountLabel }}</span>
          </span>
          <div class="mt-2" v-if="listing.available_for_framing">
            {{ listing.price_with_framing.format }}
            <span class="font-normal text-zinc-400 ml-2">+ Frame</span>
          </div>
        </span>
      </div>

      <div v-if="userConfig.isAdmin" class="justify-between flex flex-col mt-1">
        <div>{{ listing.gp1k }} gp1k</div>

        <div v-if="shortlistVisible" class="text-xs">
          <a href="#" @click.prevent="showShortlistModal = true">+ Shortlist</a>
        </div>

        <ShortlistModal
          v-if="showShortlistModal"
          @close="showShortlistModal = false"
          :listingId="listing.id"
          :apiUrl="listing.competition_results_api_url"
        />
      </div>
    </div>
  </a>
</template>

<script>
import Favourite from "./Favourite.vue"
import ShortlistModal from "@/pages/Shortlist/ShortlistModal.vue"
import Medium from "./Medium.vue"

let imagesLoading = 0
const loadQueue = []
const MAX_CONCURRENT = 8
const MAX_RETRIES = 5

// Concurrency gate: at most MAX_CONCURRENT full images fetch at once; the rest
// queue. Stops the grid firing a burst that overwhelms the CDN/origin connection.
function acquireSlot(run) {
  if (imagesLoading < MAX_CONCURRENT) {
    imagesLoading += 1
    run()
  } else {
    loadQueue.push(run)
  }
}

function releaseSlot() {
  imagesLoading = Math.max(0, imagesLoading - 1)
  const next = loadQueue.shift()
  if (next) {
    imagesLoading += 1
    next()
  }
}

// Strip the query string (signed token) before logging a URL to analytics.
function imagePath(url) {
  return (url || "").split("?")[0]
}

function imageDiagnostics(url, startedAt) {
  const connection = navigator.connection || {}
  const entries = performance.getEntriesByName(url)
  const entry = entries[entries.length - 1]
  const dimensions = (url || "").match(/\/s\/h\/[a-z_]+\/(\d+)\/(\d+)\//)
  return {
    duration_ms: startedAt ? Math.round(performance.now() - startedAt) : null,
    concurrent_loading: imagesLoading,
    queue_depth: loadQueue.length,
    online: navigator.onLine,
    dpr: window.devicePixelRatio,
    viewport_width: window.innerWidth,
    image_width: dimensions ? Number(dimensions[1]) : null,
    image_height: dimensions ? Number(dimensions[2]) : null,
    effective_type: connection.effectiveType ?? null,
    rtt: connection.rtt ?? null,
    downlink: connection.downlink ?? null,
    save_data: connection.saveData ?? null,
    response_status: entry?.responseStatus ?? null,
    transfer_size: entry?.transferSize ?? null,
    protocol: entry?.nextHopProtocol || null,
  }
}

export default {
  name: "Listing.Item",
  data() {
    return {
      inCart: false,
      showShortlistModal: false,
      imageSrc: this.listing.images.artworks.tiny,
      fullSrcset: null,
    }
  },
  watch: {
    "listing.id"() {
      // Reused for a different listing: release the previous load so its slot,
      // retry timer, queued retry and attempt counter don't leak into the new one.
      this.cleanupLoad()
      this._attempts = 0
      this.imageSrc = this.listing.images.artworks.tiny
      this.fullSrcset = null
    },
  },
  props: {
    soldRibbon: {
      type: Boolean,
      required: false,
      default: true,
    },
    listing: {
      type: Object,
      required: true,
      default: {},
    },
    priceFilterApplied: {
      type: Boolean,
      default: false,
    },
    sizeFilterApplied: {
      type: Boolean,
      default: false,
    },
    guestFavourites: {
      type: Array,
      required: false,
      default: [],
    },
  },
  mounted() {
    this.inCart = this.listing.in_cart
  },
  beforeDestroy() {
    this._destroyed = true
    this.cleanupLoad()
  },
  methods: {
    addToCart() {
      this.inCart = true
    },
    cleanupLoad() {
      clearTimeout(this._retryTimer)
      this._retryTimer = null
      const queued = loadQueue.indexOf(this._queuedRun)
      if (queued > -1) {
        loadQueue.splice(queued, 1)
      }
      if (this._inFlight) {
        releaseSlot()
      }
      this._inFlight = false
      this._queuedRun = null
    },
    loadFullImage() {
      this._queuedRun = () => this.startFullLoad()
      acquireSlot(this._queuedRun)
    },
    startFullLoad(retry) {
      if (this._destroyed) {
        releaseSlot()
        return
      }
      // Image names reflect grid slot width, not device type:
      //   desktop (295w) → smallest file, used on mobile/tablet (small slots, DPR=1)
      //   tablet  (353w) → mid-size file, used on desktop (320px slots)
      //   mobile  (450w) → largest file, used on large desktops + Retina devices
      // sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" matches the
      // 4-col / 3-col / 2-col grid layout so the browser picks the right file automatically.
      // r=N forces a fresh fetch on retry; the CDN strips it so the signature stays valid.
      // Use & when the signed URL already has a query string (it always does).
      const { mobile, tablet, desktop } = this.listing.images.artworks
      const withRetry = (url) => (retry ? `${url}${url.includes("?") ? "&" : "?"}r=${retry}` : url)
      this.fullSrcset = `${withRetry(mobile)} 450w, ${withRetry(tablet)} 353w, ${withRetry(desktop)} 295w`
      this.imageSrc = withRetry(mobile)
      this._fullStart = performance.now()
      this._inFlight = true
    },
    handleTinyError() {
      const url = this.listing.images.artworks.tiny
      window.posthog?.capture("listing_image_error", {
        type: "tiny",
        listing_id: this.listing.id,
        url: imagePath(url),
        ...imageDiagnostics(url, null),
      })
    },
    handleFullError(event) {
      // Ignore a stale/duplicate event: if the slot was already released (e.g. the
      // card was reused and cleanupLoad ran), don't release it again or mutate the
      // new listing's retry state.
      if (!this._inFlight) return
      this._inFlight = false
      releaseSlot()
      this._attempts = (this._attempts || 0) + 1
      const willRetry = this._attempts <= MAX_RETRIES
      const url = event?.target?.currentSrc || this.imageSrc
      window.posthog?.capture("listing_image_error", {
        type: "full",
        listing_id: this.listing.id,
        url: imagePath(url),
        is_retry: this._attempts > 1,
        attempt: this._attempts,
        final: !willRetry,
        ...imageDiagnostics(url, this._fullStart),
      })
      if (willRetry) {
        const n = this._attempts
        const delay = Math.min(8000, 1000 * 2 ** (n - 1)) + Math.floor(Math.random() * 1000)
        this._queuedRun = () => this.startFullLoad(n)
        this._retryTimer = setTimeout(() => acquireSlot(this._queuedRun), delay)
      }
    },
    handleFullLoad(event) {
      // Stale callback after the slot was already released — ignore (see handleFullError).
      if (!this._inFlight) return
      this._inFlight = false
      releaseSlot()
      if (Math.random() < 0.1) {
        const url = event?.target?.currentSrc || this.imageSrc
        window.posthog?.capture("listing_image_loaded", {
          listing_id: this.listing.id,
          is_retry: (this._attempts || 0) > 0,
          attempt: (this._attempts || 0) + 1,
          ...imageDiagnostics(url, this._fullStart),
        })
      }
    },
  },
  computed: {
    shortlistVisible() {
      return this.listing.competition_category
    },
    availabilityMode() {
      if (!this.listing.reprint && (this.listing.dispatch_from_date || this.listing.artist.holiday_mode)) {
        return "modal"
      }
    },
    listingUrl() {
      if (this.listing.reprint && !this.listing.sold && !this.priceFilterApplied && !this.sizeFilterApplied) {
        return this.listing.url
      }
      return this.listing.main_listing_result_url
    },
    priceLabel() {
      const price = (this.listing.price_with_discount || this.listing.price).format

      if (this.listing.reprint) return `from ${price}`

      return price
    },
  },
  components: {
    Medium,
    Favourite,
    ShortlistModal,
  },
}
</script>
